import posthog from 'posthog-js';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR, { SWRConfig } from 'swr';

import { CliSession } from '../../../../../../typings/CliSession.interface';
import {
  DestinationType,
  DestinationTypeSchema,
  SourceType,
  SourceTypeSchema,
} from '../../../../../../typings/Integration.interface';
import {
  APIOrganization,
  APIOrganizationWithRoleAndPlan,
} from '../../../../../../typings/Organization.interface';
import { OrganizationRole } from '../../../../../../typings/OrganizationMember.interface';
import { APISubscription } from '../../../../../../typings/Subscription.interface';
import { APITeam, APITeamWithRole } from '../../../../../../typings/Team.interface';
import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { User } from '../../../../../../typings/User.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import {
  dashboard_session_initial_values,
  DashboardSession,
} from '../../../typings/DashboardSession.interface';
import { storage_keys } from '../../../utils/storage';
import Icon from '../../common/base/Icon';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import { UserContext } from '../../contexts/UserContext';
import useEventListener from '../../hooks/useEventListener';
import { useIsDegradedBanner } from '../../hooks/useIsDegradedBanner';
import useLocalStorage from '../../hooks/useLocalStorage';
import useSearchQuery from '../../hooks/useSearchQuery';
import FullscreenLayout from '../../layouts/FullscreenLayout';

export const DashboardContext = createContext<{
  active_cli_sessions: CliSession[];
  user?: User | null;
  team?: APITeam | null;
  organizations?: APIOrganizationWithRoleAndPlan[] | null;
  teams?: APITeamWithRole[] | null;
  mutateTeams: () => Promise<APITeamWithRole[] | undefined>;
  mutateOrganizations: () => Promise<APIOrganizationWithRoleAndPlan[] | undefined>;
  organization?: APIOrganization | null;
  source_types?: {
    [key in SourceType]: SourceTypeSchema;
  };
  destination_types?: {
    [key in DestinationType]: DestinationTypeSchema;
  };
  team_role?: TeamRole;
  organization_role?: OrganizationRole;
  subscription?: APISubscription;
  resetContext: () => void;
  mutateTeam: (team: APITeam) => void;
  mutateSubscription: (subscription: APISubscription) => void;
  mutateOrganization: (organization: APIOrganization) => void;
  session: DashboardSession;
  setSession: (session: DashboardSession) => void;
  has_sources: boolean;
  has_connection: boolean;
  has_created_connections: boolean;
  mutateHasConnection: () => void;
  get_started_pending_steps: number;
  has_team_members_or_invites: boolean;
  show_get_started: boolean;
  setShowGetStarted: (show: boolean) => void;
  prev_location: { pathname: string; search: string };
  nav_collapsed: boolean;
  setNavCollapsed: (collapsed: boolean) => void;
  is_degraded_banner_active: boolean;
  dismissDegradedBanner: () => void;
  logout: () => void;
}>({
  active_cli_sessions: [],
  mutateTeams: async () => undefined,
  mutateOrganizations: async () => undefined,
  mutateTeam: () => {
    return;
  },
  mutateSubscription: () => {
    return;
  },
  mutateOrganization: () => {
    return;
  },
  resetContext: () => {
    return;
  },
  logout: () => {
    return;
  },
  session: {} as DashboardSession,
  setSession: () => {
    return;
  },
  has_sources: false,
  has_connection: false,
  has_created_connections: false,
  mutateHasConnection: () => {
    return;
  },
  get_started_pending_steps: 2,
  has_team_members_or_invites: false,
  show_get_started: true,
  setShowGetStarted: () => {
    return;
  },
  prev_location: { pathname: '', search: '' },
  nav_collapsed: false,
  setNavCollapsed: () => {
    return;
  },
  is_degraded_banner_active: false,
  dismissDegradedBanner: () => {
    return;
  },
});

const getAddTeamKeyMiddleWare = (team_id?: string) => (useSWRNext) => {
  return (key, fetcher, config) => {
    return typeof key === 'function'
      ? useSWRNext((...args) => [key(...args), team_id], fetcher, config)
      : useSWRNext(key && [key, team_id], fetcher, config);
  };
};

let prev_location_cache = null;

export const DashboardContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { HookdeckAPI, setAPITeamId } = useContext(GlobalContext);
  const { loading_user, user } = useContext(UserContext);

  const history = useHistory();
  const location = useLocation();
  const toasts = useToasts();
  const search = useSearchQuery<{ team_id: string }>();
  const [prev_location, setPrevLocation] = useState(location);

  const {
    data: session_context,
    mutate: mutateContext,
    error: context_error,
  } = useSWR(
    HookdeckAPI.team_id && [APIMethodKeys.session.context(), HookdeckAPI.team_id],
    () => HookdeckAPI.session.context(),
    {
      shouldRetryOnError: false,
      onSuccess: (context) => {
        setLastTeamId(context.team.id);
        setSession({ ...session, team_id: context.team.id });
      },
    },
  );

  const { team, organization, subscription, team_role, organization_role } = session_context || {};
  const is_eg_type = !!team && team.type !== 'outpost';

  const [session, setSession] = useLocalStorage<DashboardSession>(
    `${storage_keys.session.dashboard_session}:${user?.id}`,
    dashboard_session_initial_values,
    true,
  );
  const [last_team_id, setLastTeamId] = useLocalStorage<string | null>(
    `${storage_keys.api_last_team_id}:${user?.id}`,
    null,
  );
  const [show_get_started, setShowGetStarted] = useLocalStorage<boolean>(
    `${storage_keys.show_get_started}:${user?.id}`,
    true,
  );

  const [nav_collapsed, setNavCollapsed] = useLocalStorage<boolean>('pref:nav_collapsed', false);
  const { is_degraded_banner_active, setDismissedAt: dismissDegradedBanner } =
    useIsDegradedBanner();

  useEffect(() => {
    setPrevLocation(prev_location_cache || location);
    prev_location_cache = location;
  }, [location]);

  const { data: teams, mutate: mutateTeams } = useSWR(APIMethodKeys.teams.listAll(), () =>
    HookdeckAPI.teams.listAll(),
  );

  const { data: source_types } = useSWR(
    is_eg_type && HookdeckAPI.team_id && APIMethodKeys.sourceTypes.list(),
    () => HookdeckAPI.sourceTypes.list(),
  );

  const { data: destination_types } = useSWR(
    is_eg_type && HookdeckAPI.team_id && APIMethodKeys.destinationTypes.list(),
    () => HookdeckAPI.destinationTypes.list(),
  );

  const {
    data: organizations,
    mutate: mutateOrganizations,
    error: organizations_error,
  } = useSWR(APIMethodKeys.organizations.listAll(), () => HookdeckAPI.organizations.listAll());

  const { data: webhooks, mutate: mutateHasConnection } = useSWR(
    is_eg_type &&
      HookdeckAPI.team_id && [APIMethodKeys.webhooks.list({ limit: 1 }), HookdeckAPI.team_id],
    () => HookdeckAPI.webhooks.list({ limit: 1 }),
  );

  const { data: members } = useSWR(
    organization && APIMethodKeys.organizations.listMembers(organization.id),
    () => HookdeckAPI.organizations.listMembers(),
  );
  const { data: invites } = useSWR(
    organization && APIMethodKeys.organizations.listMemberInvites(organization!.id),
    () => HookdeckAPI.organizations.listMemberInvites(),
  );

  const { data: sources } = useSWR(is_eg_type && APIMethodKeys.sources.list(), () =>
    HookdeckAPI.sources.list(),
  );
  const { data: destinations } = useSWR(is_eg_type && APIMethodKeys.destinations.list(), () =>
    HookdeckAPI.destinations.list(),
  );

  const has_sources = !!sources && sources?.count > 0;
  const has_connection = !!webhooks && webhooks?.count > 0;
  const has_created_connections = !!(!!sources?.count || !!destinations?.count);
  const has_team_members_or_invites =
    (!!members && members?.length > 1) || (!!invites && invites?.length > 0);
  const get_started_pending_steps = (() => {
    let total_pending_steps = 2;
    if (has_connection) {
      total_pending_steps -= 1;
    }
    if (has_team_members_or_invites) {
      total_pending_steps -= 1;
    }

    return total_pending_steps;
  })();

  const { data: active_cli_sessions } = useSWR(
    is_eg_type && user && team && [APIMethodKeys.cli_clients.list({ active: true }), team.id],
    () => HookdeckAPI.cli_clients.getSessions({ active: true }),
    { refreshInterval: 10000 },
  );

  // make sure that the last team id is synced with the current window team id
  const windowRef = useEventListener('focus', () => {
    if (HookdeckAPI.team_id) {
      setLastTeamId(HookdeckAPI.team_id);
    }
  });

  const clearContextStates = () => {
    setAPITeamId(null);
    setLastTeamId(null);
    setSession({ team_id: null });
    mutateTeams(undefined);
    mutateContext(undefined);
    mutateOrganizations(undefined);
  };

  const resetContext = () => {
    clearContextStates();
    return history.replace('/projects');
  };

  useEffect(() => {
    windowRef.current = window;

    const team_id = search.query.team_id ?? session.team_id ?? last_team_id ?? user?.last_team_id;

    if (!team_id && teams && teams.length > 0) {
      setAPITeamId(teams[0].id);
    }

    // Only set the team ID from session/localStorage if it hasn't been explicitly set yet
    // (initial load). When HookdeckAPI.team_id is already set (e.g. via handleTeamChange),
    // don't override it with potentially stale session/localStorage values — the
    // session.context() SWR may not have resolved yet to update them.
    if (team_id && !HookdeckAPI.team_id) {
      setAPITeamId(team_id);
    }

    // Always honor explicit team_id from URL search params
    if (search.query.team_id && search.query.team_id !== HookdeckAPI.team_id) {
      setAPITeamId(search.query.team_id);
    }
  }, [teams]);

  useEffect(() => {
    if (
      user &&
      context_error &&
      context_error?.status === 401 &&
      !location.pathname.includes('/projects') &&
      !location.pathname.includes('/onboarding')
    ) {
      if (session.team_id || last_team_id) {
        toasts.addToast('warning', 'Unauthorized to access the latest project.');
      }
      resetContext();
    }
  }, [user && context_error && context_error?.status === 401]);

  // Handle MFA setup required error (403 with error code 'mfa_setup_required')
  // This catches the case where a user is already logged in and opens the dashboard,
  // but their organization requires MFA and they haven't set it up yet
  useEffect(() => {
    // Check both organizations_error and context_error for mfa_setup_required
    const mfa_error = [organizations_error, context_error].find(
      (err) =>
        err?.status === 403 &&
        typeof err?.response === 'object' &&
        err?.response !== null &&
        (err?.response as { error?: string })?.error === 'mfa_setup_required',
    );

    if (
      mfa_error &&
      !location.pathname.includes('/setup-mfa') &&
      !location.pathname.includes('/signin')
    ) {
      // Extract organization-specific info from the error response
      const error_response = mfa_error.response as {
        blocking_organization?: { id: string; name: string };
        redirect?: string;
      };
      const blocking_org = error_response?.blocking_organization;

      // Build redirect URL with organization context
      const setup_url = new URL('/setup-mfa', window.location.origin);
      if (blocking_org?.id) {
        setup_url.searchParams.set('organization_id', blocking_org.id);
      }
      // Preserve the intended destination for after MFA setup
      setup_url.searchParams.set('redirect', location.pathname + location.search);

      window.location.replace(setup_url.toString());
    }
  }, [
    organizations_error?.status,
    organizations_error?.response,
    context_error?.status,
    context_error?.response,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (
      !teams ||
      !organizations ||
      !user ||
      (HookdeckAPI.team_id && !session_context && !context_error)
    ) {
      return;
    }

    if (user!.is_guest) {
      if (window.location.pathname.indexOf('/cli/events') === 0) {
        return window.location.replace(process.env.CONSOLE_URL!);
      }
      return window.location.replace('/signup');
    }

    if (
      teams.length === 0 &&
      !organization &&
      !team &&
      !location.pathname.includes('/onboarding') &&
      !location.pathname.includes('/cli-auth')
    ) {
      if (organizations.length > 0) {
        return history.replace('/projects');
      }
      // Only preserve redirect if user was trying to access a specific path (not just '/')
      // This ensures CLI auth flows work while normal signups go to /create-first-connection.
      // CliAuth redirects to onboarding when the user has no projects (non-guest-claim flows).
      const redirect_param =
        location.pathname !== '/'
          ? encodeURIComponent(`${location.pathname}${location.search}`)
          : '';

      return history.replace(
        redirect_param ? `/onboarding?redirect=${redirect_param}` : '/onboarding',
      );
    }
  }, [user, teams, organizations, session_context, context_error?.response]);

  useEffect(() => {
    if (organization) {
      posthog?.group('organization', organization.id);
    }
    if (team) {
      posthog?.group('team', team.id);
    }

    if ((window as any).$crisp) {
      (window as any).$crisp.push([
        'set',
        'session:data',
        [
          [
            ['organization_id', organization?.id || ''],
            ['organization_name', organization?.name || ''],
            [
              'organization_crm_url',
              organization ? `https://crm.hookdeck.com/companies/${organization.id}` : '',
            ],
            ['feature_flags', JSON.stringify(organization?.feature_flags || {})],
            ['team_id', team?.id || ''],
            ['team_name', team?.name || ''],
            ['max_events_per_second', team?.max_events_per_second || ''],
            ['retention_days', subscription?.retention_days || ''],
            ['plan', subscription?.plan || ''],
            ['outpost_plan', subscription?.outpost_plan || ''],
          ],
        ],
      ]);
    }
  }, [team, subscription, organization]);

  const logout = () => {
    clearContextStates();
    window.localStorage.clear();
    window.sessionStorage.clear();
    window.location.replace('/signin');
  };

  const context = {
    active_cli_sessions: active_cli_sessions || [],
    user,
    team: team || (teams ? (teams.length > 0 ? teams[0] : null) : undefined),
    source_types,
    destination_types,
    organization,
    organizations,
    teams,
    team_role: team_role,
    organization_role: organization_role,
    subscription: subscription,
    // Plural mutators revalidate list endpoints and should be awaited when downstream UI
    // depends on the refreshed collection state.
    mutateTeams: () => mutateTeams(),
    mutateOrganizations: () => mutateOrganizations(),
    mutateTeam: (team: APITeam) => {
      mutateContext((session_context) => ({ ...session_context, team }) as any);
      mutateTeams((current_teams) => {
        // Teams cache can be undefined during initial load/reset or key transitions.
        // In that case, skip optimistic list patching and rely on the next /teams revalidation.
        if (current_teams === undefined) {
          return current_teams;
        }

        // Keep session_context.team and teams.listAll consistent for updates on cached teams.
        // New teams should come from the authoritative list fetch to preserve role metadata.
        return current_teams.map((current_team) =>
          current_team.id === team.id ? { ...current_team, ...team } : current_team,
        );
      }, false);
    },
    mutateSubscription: (subscription: APISubscription) =>
      mutateContext((session_context) => ({ ...session_context, subscription }) as any),
    mutateOrganization: (organization: APIOrganization) =>
      mutateContext((session_context) => ({ ...session_context, organization }) as any),
    resetContext: resetContext,
    session,
    setSession,
    has_sources,
    has_connection,
    has_created_connections,
    mutateHasConnection,
    has_team_members_or_invites,
    get_started_pending_steps,
    show_get_started,
    setShowGetStarted,
    prev_location,
    nav_collapsed,
    setNavCollapsed,
    is_degraded_banner_active,
    dismissDegradedBanner,
    logout,
  };

  if (
    loading_user ||
    (!context_error &&
      teams === undefined &&
      organization === undefined &&
      team === undefined &&
      subscription === undefined &&
      (webhooks === undefined || session_context === undefined))
  ) {
    return (
      <FullscreenLayout center no_decorations>
        <Icon icon="loading" muted />
      </FullscreenLayout>
    );
  }

  return (
    <DashboardContext.Provider value={context}>
      <SWRConfig
        key={team?.id}
        value={{
          use: [getAddTeamKeyMiddleWare(HookdeckAPI.team_id)],
        }}>
        {children}
      </SWRConfig>
    </DashboardContext.Provider>
  );
};

export const WithUserOnly: React.FC<PropsWithChildren> = ({ children }) => {
  const { user, user_error } = useContext(UserContext);

  useEffect(() => {
    if (user_error?.status === 401) {
      // Preserve the full path + query parameters when redirecting to signin
      // This ensures logged-out users can return to their intended destination after authentication
      const redirect_param = encodeURIComponent(`${location.pathname}${location.search}`);
      return window.location.replace(`/signin?redirect=${redirect_param}`);
    }
  }, [user_error]);

  return user ? (
    <>{children}</>
  ) : (
    <FullscreenLayout center no_decorations>
      <Icon icon="loading" muted />
    </FullscreenLayout>
  );
};

export const WithTeamOnly = ({ children }) => {
  const { team, subscription } = useContext(DashboardContext);

  if (!team || !subscription) {
    return (
      <FullscreenLayout center no_decorations>
        <Icon icon="loading" muted />
      </FullscreenLayout>
    );
  }

  return children;
};
